import type {ReactElement} from 'react';
import React, {useMemo, useRef, useState} from 'react';
import {Text, TextSize} from '@Components/text';
import {ClickableDiv} from '@Components/clickable-div';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {
  useEventWizardCoverPhoto,
  useEventWizardDoesPAYGUserHaveAITemplateLimit,
  useEventWizardEndDateTime,
  useEventWizardIsCoverPhotoPreviewLoading,
  useEventWizardOnlineVenue,
  useEventWizardSelectedType,
  useEventWizardShouldCheckErrState,
  useEventWizardStartDateTime,
  useEventWizardTitle,
  useEventWizardVenue,
} from '@Components/event-wizard/event-wizard.hooks';
import {Button, Size, Type} from '@Components/button';
import {
  getEventWizardMediaContainerTitle,
  onRemoveMedia,
  openDesignSelector,
  openEmbeddedEditor,
  openGalleryTemplateSelector,
  openUploadSelector,
  shouldUpsellAITemplateTagging,
} from '@Components/event-wizard/event-wizard-image-video/event-wizard-image-video.helper';
import {isCoverPhotoSectionComplete} from '@Components/event-wizard/event-wizard-review/event-wizard-review.helper';
import {EventWizardErrorInfo} from '@Components/event-wizard/event-wizard-error-info';
import {ImageBlurBackground} from '@Components/event-wizard/image-blur-background';
import styles from './event-wizard-image-video.module.scss';
import type {EventTypeAiSearchParams} from './event-wizard-image-video.types';
import {MediaTypes} from './event-wizard-image-video.types';
import {authenticateUser, getUserId, isUserPremium} from '@Libraries/user.library';
import {EventVenueType} from '@Components/event-wizard/event-wizard-venue/event-wizard-venue.types';
import {getDurationString} from '@Utils/date.util';
import {SkeletonBrick} from '@Components/skeleton-brick';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import {getAssetUrl} from '@Utils/s3.util';

export function EventWizardImageVideo(): ReactElement<any> {
  const coverPhoto = useEventWizardCoverPhoto();
  const isCoverPhotoPreviewLoading = useEventWizardIsCoverPhotoPreviewLoading();
  const shouldCheckErrState = useEventWizardShouldCheckErrState();
  const [uploadedMediaType, setUploadedMediaType] = useState<MediaTypes | undefined>(coverPhoto.mediaType);
  const eventName = useEventWizardTitle();
  const selectedStartDateTime = useEventWizardStartDateTime();
  const selectedEndDateTime = useEventWizardEndDateTime();
  const eventTimings = `${selectedStartDateTime.toTimeString()} to ${selectedEndDateTime.toTimeString()}`;
  const eventDuration = useMemo((): string => {
    return getDurationString(selectedStartDateTime, selectedEndDateTime);
  }, [selectedStartDateTime, selectedEndDateTime]);
  const {selectedVenueType, offlineVenue} = useEventWizardVenue();
  const onlineEventURL = useEventWizardOnlineVenue() ?? '';
  const eventLocation = selectedVenueType === EventVenueType.OFFLINE ? (offlineVenue?.name ?? '') : onlineEventURL;
  const eventDates = `${selectedStartDateTime.toDateString()} to ${selectedEndDateTime.toDateString()}`;
  const selectedEventType = useEventWizardSelectedType();
  const eventType = selectedEventType?.name ?? '';
  const isUserLoggedIn = useRef<boolean>(typeof getUserId() !== 'undefined');
  const isPremium = useRef(isUserLoggedIn.current && isUserPremium());
  const doesPAYGUserHaveAITemplateLimit = useEventWizardDoesPAYGUserHaveAITemplateLimit();

  const [coverPhotoOptionLoading, setCoverPhotoOptionLoading] = useState<MediaTypes | null>(null);

  const isInErrState = (): boolean => {
    return shouldCheckErrState && !isCoverPhotoSectionComplete(coverPhoto);
  };

  const stopCoverPhotoOptionLoading = (): void => {
    setCoverPhotoOptionLoading(null);
    window.removeEventListener(window.PMW.getEventDialogCloseEvent(), stopCoverPhotoOptionLoading);
  };

  const openAuthenticatedUserMediaSelectionFlow = (mediaType: MediaTypes | undefined): void => {
    if (mediaType === undefined || mediaType === MediaTypes.UPLOAD) {
      return;
    }

    void authenticateUser(() => {
      onAuthenticatedUserMediaSelectionFlow(mediaType);
    }, stopCoverPhotoOptionLoading);
  };

  const onAuthenticatedUserMediaSelectionFlow = (mediaType: MediaTypes): void => {
    setCoverPhotoOptionLoading(mediaType);

    if (mediaType === MediaTypes.MY_DESIGNS) {
      openDesignSelector(stopCoverPhotoOptionLoading);
    } else if (mediaType === MediaTypes.TEMPLATES) {
      const aiSearchParams: EventTypeAiSearchParams | undefined = selectedEventType !== null ? {eventName, eventTypeHashedId: selectedEventType.hashedID} : undefined;
      openGalleryTemplateSelector(
        stopCoverPhotoOptionLoading,
        aiSearchParams,
        eventName.length > 0 ? {eventDates, eventName, eventDuration, eventTimings, eventLocation, eventType} : undefined,
        shouldUpsellAITemplateTagging(doesPAYGUserHaveAITemplateLimit)
      );
    }
  };

  const onClickMediaContainer = (mediaType: MediaTypes | undefined): void => {
    setUploadedMediaType(mediaType);

    if (mediaType === undefined) {
      return;
    }
    window.addEventListener(window.PMW.getEventDialogCloseEvent(), stopCoverPhotoOptionLoading);

    if (mediaType === MediaTypes.UPLOAD) {
      stopCoverPhotoOptionLoading();
      openUploadSelector();
      return;
    }
    openAuthenticatedUserMediaSelectionFlow(mediaType);
  };

  const getContainerView = (mediaType: MediaTypes): ReactElement<any> => {
    return (
      <ClickableDiv
        onClick={() => {
          onClickMediaContainer(mediaType);
        }}
        className={`${styles.mediaTypeContainer} ${mediaType === MediaTypes.TEMPLATES ? styles.aiContainer : 'border-s-dark'} flex-column-align-center justify-content-center radius-8 _full-width`}
      >
        {getContainerMediaCoverView(mediaType)}
        {getContainerMediaTitle(mediaType)}
      </ClickableDiv>
    );
  };

  const getContainerMediaTitle = (mediaType: MediaTypes): ReactElement<any> => {
    const isAiContent = mediaType === MediaTypes.TEMPLATES;

    return (
      <div className="flex-row-align-center">
        <Text className={isAiContent ? 'content-ai' : 'content-body'} val={getEventWizardMediaContainerTitle(mediaType)} size={TextSize.XXSMALL} />
        {isAiContent ? <Icon className="content-ai spacing-m-l-1" icon="icon-ai-caption" size={IconSize.SIZE_ICON_16} /> : undefined}
      </div>
    );
  };

  const getContainerMediaCoverView = (mediaType: MediaTypes): ReactElement<any> => {
    if (mediaType === MediaTypes.TEMPLATES) {
      return (
        <div className={`${styles.mediaImgContainer} -text-align-center align-content-center spacing-m-b-1`}>
          {coverPhotoOptionLoading === mediaType ? (
            <CircularProgressLoader size={LOADER_SIZE.DEFAULT} color={LOADER_COLOR.DARK} />
          ) : (
            <img className="_full-height _full-width" src={getAssetUrl('event-wizard-template-design.png')} alt="template-design" />
          )}
        </div>
      );
    }

    if (coverPhotoOptionLoading === mediaType) {
      return (
        <div className={`${styles.optionLoader} -text-align-center align-content-center spacing-m-b-1 _full-width`}>
          <CircularProgressLoader size={LOADER_SIZE.DEFAULT} color={LOADER_COLOR.DARK} />
        </div>
      );
    }
    const icon = mediaType === MediaTypes.MY_DESIGNS ? 'icon-designs' : 'icon-upload';
    return <Icon className="spacing-m-b-1" icon={icon} type={IconType.FLAT} shape={IconShape.CIRCLE} size={IconSize.SIZE_ICON_20} />;
  };

  const openDesignEditor = (): void => {
    if (uploadedMediaType !== undefined && !!coverPhoto.selectedTemplateHID) {
      openEmbeddedEditor(coverPhoto.selectedTemplateHID, uploadedMediaType, undefined, isPremium.current);
    }
  };

  const getImageOptionCTABtns = (): ReactElement<any> => {
    if (isCoverPhotoPreviewLoading) {
      return (
        <div className="flex-column-align-start">
          <SkeletonBrick className="spacing-m-b-2" width={108} height={32} />
          <SkeletonBrick className="spacing-m-b-2" width={108} height={32} />
          <SkeletonBrick width={108} height={32} />
        </div>
      );
    }

    return (
      <>
        {coverPhoto.selectedTemplateHID !== undefined && coverPhoto.mediaType !== MediaTypes.UPLOAD ? (
          <Button
            onClick={openDesignEditor}
            customClasses="spacing-m-b-2 _full-width"
            text={window.i18next.t('pmwjs_edit_design')}
            icon="icon-pencil"
            type={Type.SECONDARY}
            size={Size.XSMALL}
          />
        ) : undefined}
        {coverPhoto.mediaType !== undefined ? (
          <Button
            onClick={() => {
              onClickMediaContainer(uploadedMediaType);
            }}
            customClasses="spacing-m-b-2 _full-width"
            text={window.i18next.t('pmwjs_replace')}
            icon="icon-replace"
            type={Type.SECONDARY}
            size={Size.XSMALL}
          />
        ) : undefined}
        <Button
          onClick={onRemoveMedia}
          customClasses="_full-width"
          text={window.i18next.t('pmwjs_remove')}
          icon="icon-delete"
          type={Type.SECONDARY}
          size={Size.XSMALL}
          iconClassName="content-danger"
        />
      </>
    );
  };

  const getMediaUploadedView = (): ReactElement<any> => {
    return (
      <>
        <Text val={window.i18next.t('pmws_add_image_or_video')} size={TextSize.XXSMALL} bold />
        <div className="flex-row-align-center justify-content-center bg-neutral-1 spacing-m-t-1 spacing-p-3 radius-12">
          <ImageBlurBackground
            maxHeight={112}
            maxWidth={112}
            url={coverPhoto.uploadedUrl}
            isLoading={isCoverPhotoPreviewLoading}
            containerClass={`${styles.imgContainer} spacing-m-r-2 radius-8 border-s-dark`}
          />
          <div className="flex-column-align-start justify-content-center">{getImageOptionCTABtns()}</div>
        </div>
      </>
    );
  };

  const getOptionsForUploadView = (): ReactElement<any> => {
    return (
      <div>
        <Text val={window.i18next.t('pmws_add_image_or_video')} size={TextSize.XXSMALL} bold />
        <div className={`${styles.uploadOptions} ${isInErrState() ? 'bg-danger-10' : 'bg-neutral-1'} flex-column-align-start radius-12 spacing-p-3 _full-width`}>
          <div className={`${styles.gap12} spacing-m-t-1 flex-row-align-center justify-content-between _full-width`}>
            {getContainerView(MediaTypes.TEMPLATES)}
            {getContainerView(MediaTypes.MY_DESIGNS)}
            {getContainerView(MediaTypes.UPLOAD)}
          </div>
          {isInErrState() ? <EventWizardErrorInfo className="spacing-m-t-3" text={window.i18next.t('pmwjs_please_add_img_proceed')} /> : undefined}
        </div>
      </div>
    );
  };

  return coverPhoto.uploadedUrl || isCoverPhotoPreviewLoading ? getMediaUploadedView() : getOptionsForUploadView();
}
